import { AuthenticatedUserContext, CollapsibleTable, InstitutionBreadcrumbs, PageLayout } from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { useNavigate } from 'react-router-dom';
import { IReportParam, ReportExportFormat, createReportUrl } from '../utils/createReportUrl';
import { Paper, styled } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import * as Dtos from '../api/dtos';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface IReportsProps {
    institutionCode?: string
}

export interface IReportItem {
    reportType: number;
    reportTitle: string;
    reportIdentifier: string;
    reportParams?: IReportParam[];
    reportFormat?: ReportExportFormat;
}

const PaperContainer = styled(Paper)(({theme}) => ({
    padding: theme.spacing(3)
}));

const SSRS_REPORT = 1;
const OPMS_REPORT = 2;

const pageSizeOptions = [5, 10, 20, 50, 100];

const INSTITUTION_BREADCRUMB_CREATOR = (institution?: Dtos.IInstitution | null) => {
    if (!institution) {
        return '/reports';
    }

    return `/reports/${institution.code}`;
}

const Reports: React.FunctionComponent<IReportsProps> = () => {
    const navigate = useNavigate();

    const {
        permissions: [canAdministerOpms]
    } = usePermissionsByIds(
        [
            Permission.OpmsAdminister, 
        ], 
        undefined, 
        undefined, 
        undefined
    );

    const { data: user } = useContext(AuthenticatedUserContext);

    const onOpmsReportClick = useCallback((report?: IReportItem | undefined) => {
        if (report) {
            navigate(`/${report.reportIdentifier}`);
        }
    }, [navigate]);

    const onSsrsReportClick = useCallback((report?: IReportItem | undefined) => {
        if (report) {
            const reportUrl: string = createReportUrl(report.reportIdentifier,
                report?.reportParams ?? [],
                report.reportFormat ? report.reportFormat as unknown as ReportExportFormat : undefined,
                true);

            window.open(reportUrl);
        }
    }, [createReportUrl]);

    //@ts-expect-error
    const onRowClick: GridEventListener<'rowClick'> = useCallback((params) => {
        if (params.row?.reportType === SSRS_REPORT) {
            onSsrsReportClick(params.row);
        } else if (params.row?.reportType === OPMS_REPORT) {
            onOpmsReportClick(params.row);
        }
    }, [onSsrsReportClick, onOpmsReportClick]);

    const reportColumns = useMemo(() => {
        const columns: Array<GridColDef<IReportItem>> = [
            {
                headerName: 'Report',
                field: 'reportTitle',
                flex: 1
            },
            {
                headerName: 'Access',
                field: 'access',
                flex: 1
            }
        ];

        return columns;
    }, []);

    const reports = useMemo(() => {
        return [
            !!canAdministerOpms && {
                id: 1,
                reportType: SSRS_REPORT,
                reportTitle: 'Forms Due Schedule',
                reportIdentifier: 'FormsDueSchedule',
                reportParams: [
                    { paramName: 'userkey', paramValue: user?.userName }
                ],
                access: "BCT Staff"
            },
            !!canAdministerOpms && {
                id: 2,
                reportType: SSRS_REPORT,
                reportTitle: 'Forms Due Summary',
                reportIdentifier: 'FormsDueSummary',
                reportParams: [
                    { paramName: 'UserKey', paramValue: user?.userName }
                ],
                access: "BCT Staff"
            },
        ].filter(x => !!x) as IReportItem[];
    }, [canAdministerOpms, user]);

    const [tableCount, setTableCount] = useState(1);

    useEffect(() => {
        setTableCount(x => x + 1);
    }, [reports, reportColumns]);

    return (
        <PageLayout
            heading="Reports"
            breadcrumbs={<InstitutionBreadcrumbs createInstitutionLink={INSTITUTION_BREADCRUMB_CREATOR}/>}
        >
            <PaperContainer>    
                <CollapsibleTable
                    key={tableCount}
                    rows={reports ?? []}
                    loading={false}
                    columns={reportColumns as any}
                    onRowClick={onRowClick}
                    pageSizeOptions={pageSizeOptions}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } }}}
                    title={"Reports"}
                    pluralizeTitle={false}
                    entityName={"Report"}
                    disabledTableText={"There are no reports"}
                    startCollapsed={false}
                />
            </PaperContainer>
        </PageLayout>
    );
};

export default Reports;
